import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import { jsx } from '@emotion/core';
import { isObject, isString, bool, uniqueArray, isArray, isFunction } from '@remirror/core-helpers';
import { isValidElement as isValidElement$1, Fragment, Children } from 'react';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/**
 * These are the constants used to determine whether an element is a remirror constant.
 */
var RemirrorType;
/**
 * A drop in replacement for built in React.isValidElement which accepts a test value of any type
 *
 * @param value - the value to check
 */

(function (RemirrorType) {
  RemirrorType["Extension"] = "extension";
  RemirrorType["SSR"] = "ssr";
  RemirrorType["EditorProvider"] = "editor-provider";
  RemirrorType["ManagedEditorProvider"] = "managed-editor-provider";
  RemirrorType["Editor"] = "editor";
  RemirrorType["Manager"] = "manager";
  RemirrorType["ManagerProvider"] = "manager-provider";
  RemirrorType["ContextProvider"] = "context-provider";
})(RemirrorType || (RemirrorType = {}));

var isValidElement = function isValidElement(value) {
  return isObject(value) && isValidElement$1(value);
};
/**
 * Check whether a react node is a built in dom element (i.e. `div`, `span`)
 *
 * @param value - the value to check
 */

var isReactDOMElement = function isReactDOMElement(value) {
  return isValidElement(value) && isString(value.type);
};
/**
 * Checks whether the element is a react fragment
 *
 * @param value - the value to check
 */

var isReactFragment = function isReactFragment(value) {
  return isObject(value) && isValidElement(value) && value.type === Fragment;
};
/**
 * Retrieve the element props for JSX Element
 *
 * @param element
 */

var getElementProps = function getElementProps(element) {
  return isValidElement(element) ? element.props : {};
};
/**
 * Utility for generating a unique class name
 *
 * @param uid
 * @param className
 */

var uniqueClass = function uniqueClass(uid, className) {
  return "".concat(className, "-").concat(uid);
};
/**
 * Utility for properly typechecking static defaultProps for a class component in react.
 *
 * ```ts
 * static defaultProps = asDefaultProps<RemirrorProps>()({
 *   initialContent: EMPTY_PARAGRAPH_NODE,
 * });
 * ```
 */

var asDefaultProps = function asDefaultProps() {
  return function (props) {
    return props;
  };
};
/**
 * Checks if this element has a type of any RemirrorComponent
 *
 * @param value - the value to check
 */

var isRemirrorElement = function isRemirrorElement(value) {
  return bool(isObject(value) && isValidElement(value) && value.type.$$remirrorType);
};

var isRemirrorElementOfType = function isRemirrorElementOfType(type) {
  return function (value) {
    return isRemirrorElement(value) && value.type.$$remirrorType === type;
  };
};
/**
 * Checks to see if this is the wrapper we've created around the RemirrorContent.Provider component.
 *
 * This is used to help determine how the Remirror component will be rendered. `getRootProps` is the main reason
 * for this, and I'm not even sure the effort is worth it.
 *
 * @param value - the value to check
 */


var isRemirrorContextProvider = isRemirrorElementOfType(RemirrorType.ContextProvider);
/**
 * Checks if this is a RemirrorExtension type. These are used to configure the extensions that determine
 * the underlying behaviour of the editor.
 *
 * @param value - the value to check
 */

var isRemirrorExtension = isRemirrorElementOfType(RemirrorType.Extension);
/**
 * Finds if this is a RemirrorProvider (which provides the RemirrorInjectedProps into the context);
 *
 * @param value - the value to check
 */

var isRemirrorProvider = isRemirrorElementOfType(RemirrorType.EditorProvider);
/**
 * Checks if this is a ManagedRemirrorProvider which pulls in the manager from the context and places it's children
 * inside the RemirrorProvider
 *
 * @param value - the value to check
 */

var isManagedRemirrorProvider = isRemirrorElementOfType(RemirrorType.ManagedEditorProvider);
/**
 * Clones an element while also enabling the css prop on jsx elements at the same time.
 * This is used for emotion which needs to inject the css property which React.cloneElement doesn't support.
 *
 * @param element - the element to clone
 * @param props - the props to pass through to the cloned element
 * @param rest - the children of the cloned element
 *
 * @returns a cloned react element with builtin support for the emotion `css` props
 */

var cloneElement = function cloneElement(element, props) {
  for (var _len = arguments.length, rest = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
    rest[_key - 2] = arguments[_key];
  }

  var children = uniqueArray([].concat(_toConsumableArray(isArray(props.children) ? props.children : props.children ? [props.children] : []), rest));
  return jsx.apply(void 0, [element.type, _objectSpread({
    key: element.key,
    ref: element.props.ref
  }, element.props, {}, props)].concat(_toConsumableArray(children)));
};
/**
 * Will throw an error if the child provided is not a function.
 *
 * @remarks
 * This is currently used in the remirror component to throw an error when the element children
 * are not a render prop. It should be called outside of render for class Components.
 *
 * @param prop - the prop to test
 */

var propIsFunction = function propIsFunction(prop) {
  if (!isFunction(prop)) {
    throw new Error('The child argument to the Remirror component must be a function.');
  }

  return true;
};
/**
 * A noop function that mimics the css emotion call but renders no output.
 *
 * @remarks
 * This is useful for enabling the library user to switch off emotion for core react elements.
 */

var cssNoOp = function cssNoOp() {
  return undefined;
};
/**
 * A css function that just returns the string.
 * This is typically used for css syntax highlighting of plain strings in editors.
 */

var css = bool(String.raw) ? String.raw : function (template) {
  return template[0];
};
/**
 * A drop in replacement for React.Children.only which provides more readable errors
 * when the child is not a react element or undefined.
 */

var oneChildOnly = function oneChildOnly(value) {
  if (!value) {
    throw new Error('This component requires ONE child component - Nothing was provided');
  }

  if (!isValidElement(value)) {
    throw new Error('This component requires ONE child component - An invalid element was provided');
  }

  return Children.only(value);
};
/**
 * Add the specified key to an element when it is a valid react element.
 *
 * This is useful when returning an array of components because a fragment isn't sufficient.
 */

var addKeyToElement = function addKeyToElement(element, key) {
  if (!isValidElement(element)) {
    return element;
  }

  return cloneElement(element, _objectSpread({}, element.props, {
    key: key
  }));
};

export { RemirrorType, addKeyToElement, asDefaultProps, cloneElement, css, cssNoOp, getElementProps, isManagedRemirrorProvider, isReactDOMElement, isReactFragment, isRemirrorContextProvider, isRemirrorElement, isRemirrorExtension, isRemirrorProvider, isValidElement, oneChildOnly, propIsFunction, uniqueClass };
//# sourceMappingURL=react-utils.esm.js.map
